'use client';

import React from 'react';
import Layout from '../../components/Layout';
import SubDocsLayout from '../../components/SubDocsLayout';
import useTranslation from '../../lib/translation';
import SEOHead from '../../components/SEOHead';

const questions = [
  {
    qKey: 'faq.questions.pricing',
    aKey: 'faq.questions.pricing.answer',
    enQ: 'How much does a website cost?',
    enA:
      'Our pricing starts from €299 for basic websites and goes up based on your specific needs. We offer three main plans: Beginner, Advanced, and Business.',
  },
  {
    qKey: 'faq.questions.timeline',
    aKey: 'faq.questions.timeline.answer',
    enQ: 'How long does it take to build a website?',
    enA:
      'Typically, websites take 2–6 weeks depending on complexity. We provide a specific timeline during our initial consultation.',
  },
  {
    qKey: 'faq.questions.cms',
    aKey: 'faq.questions.cms.answer',
    enQ: 'Can I update my website content myself?',
    enA:
      'Yes. We provide a user-friendly Content Management System (CMS) that allows you to update text, images, and other content without technical knowledge.',
  },
  {
    qKey: 'faq.questions.gbpTime',
    aKey: 'faq.questions.gbpTime.answer',
    enQ: 'How long does it take to see results in Google Maps (GBP)?',
    enA:
      'For local ranking (GBP), early signs of movement often appear within 30–60 days. However, reaching the Local Pack (Top 3) typically takes 3–6 months depending on your competition and location.',
  },
  {
    qKey: 'faq.questions.seoVsGbp',
    aKey: 'faq.questions.seoVsGbp.answer',
    enQ: 'What is the difference between SEO and GBP?',
    enA:
      'SEO (Search Engine Optimization) focuses on ranking your website in general search results. GBP (Local SEO) specifically targets the Map Pack and local searches to ensure customers in your physical area find you first.',
  },
  {
    qKey: 'faq.questions.needWebsite',
    aKey: 'faq.questions.needWebsite.answer',
    enQ: 'Do I need a new website for SEO/GBP to work?',
    enA:
      'Not necessarily. We can optimize your existing site. However, if your current site is slow or not mobile-friendly, we may suggest a Growth Engine upgrade so search engines do not penalize your rankings.',
  },
  {
    qKey: 'faq.questions.authIssue',
    aKey: 'faq.questions.authIssue.answer',
    enQ: 'I cannot register or login to my account. What should I do?',
    enA:
      'First, ensure you have verified your email address via the link sent to your inbox. If you still face issues, please contact us or email info@lbvisible.com—we will fix it within 24 hours.',
  },
];

const FAQ = () => {
  const { t, lang } = useTranslation('common');

  const mainEntities = questions.map(({ qKey, aKey, enQ, enA }) => ({
    '@type': 'Question',
    name:
      lang === 'lt'
        ? (t(`${qKey}.question`) as string)
        : enQ,
    acceptedAnswer: {
      '@type': 'Answer',
      text:
        lang === 'lt'
          ? (t(aKey) as string)
          : enA,
    },
  }));

  return (
    <Layout>
      <SEOHead
        title={lang === 'lt' ? Array.isArray(t('faq.pageTitle')) ? (t('faq.pageTitle') as string[]).join(' ') : (t('faq.pageTitle') as string) : 'FAQ | LB Websites'}
        description={
          lang === 'lt'
            ? Array.isArray(t('faq.description')) ? (t('faq.description') as string[]).join(' ') : (t('faq.description') as string)
            : 'Frequently asked questions about LB Websites. Learn more about pricing, launch time, and content delivery.'
        }
        keywords='FAQ, frequently asked questions, website development, pricing, support, LB Websites'
        canonicalUrl={lang === 'lt' ? '/lt/sub/duk' : '/sub/faq'}
        lang={lang}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: mainEntities,
        }}
      />

      <SubDocsLayout>
        <h1 className='text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-10 text-left'>
          {lang === 'lt' ? t('faq.heading') : 'Frequently Asked Questions'}
        </h1>

        <div className='space-y-8'>
          {questions.map(({ qKey, aKey, enQ, enA }) => (
            <div key={qKey}>
              <h2 className='font-semibold text-gray-900 dark:text-white mb-2'>
                {lang === 'lt' ? t(qKey + '.question') : enQ}
              </h2>
              <p className='text-sm text-gray-600 dark:text-gray-400 leading-relaxed'>
                {lang === 'lt' ? t(aKey) : enA}
              </p>
            </div>
          ))}
        </div>
      </SubDocsLayout>
    </Layout>
  );
};

export default FAQ;
